/**
 * connectionSeed.ts — storage seeds for connection resolution.
 *
 * Persisted selection is deliberately minimal — two keys:
 *   - ACTIVE_CONNECTION_ID (sessionStorage): this tab's selection. Tab-scoped
 *     on purpose: two tabs can view two providers independently. Mirrors the
 *     store's RESOLVED connection (one direction: resolution → storage) and
 *     seeds resolution on the next load; it is never an authority that can
 *     override the live connection list.
 *   - DEFAULT_CONNECTION_ID:{userId} (localStorage): cross-session seed,
 *     written on explicit switch / OAuth completion, cleared on sign-out.
 *
 * These are resolution SEEDS, not request-time identity: GraphQL operations
 * self-identify via the per-operation viewKey variable (graphql/apollo.ts),
 * and nothing on the transport path reads these keys. Writers: the connection
 * store's mirror effect plus the explicit transitions (OAuth completion,
 * switch, sign-out, disconnect). Readers: store boot-time resolution and the
 * auth/DDB debug pages.
 */

// Exported for the auth-debug page, which audits the raw storage rows.
export const ACTIVE_CONNECTION_ID_KEY = "dc_active_connection_id";
export const DEFAULT_CONNECTION_ID_KEY_PREFIX = "dc_default_connection_id:";

/**
 * The connection store resolves its durable seed asynchronously on a hard load.
 * That first null -> id transition attributes the already-loaded URL; it is not
 * a user/provider switch and must not consume project/family deep-link intent.
 */
export function isInitialConnectionAttribution(input: {
  attributionPending: boolean;
  previousConnectionId: string | null;
  nextConnectionId: string | null;
}): boolean {
  return (
    input.attributionPending &&
    input.previousConnectionId === null &&
    input.nextConnectionId !== null
  );
}

export function setActiveConnectionId(connectionId: string | null): void {
  if (connectionId) {
    sessionStorage.setItem(ACTIVE_CONNECTION_ID_KEY, connectionId);
  } else {
    sessionStorage.removeItem(ACTIVE_CONNECTION_ID_KEY);
  }
}

export function getActiveConnectionId(): string | null {
  return sessionStorage.getItem(ACTIVE_CONNECTION_ID_KEY);
}

export function setDefaultConnectionIdForUser(userId: string, connectionId: string | null): void {
  const key = `${DEFAULT_CONNECTION_ID_KEY_PREFIX}${userId}`;
  if (connectionId) {
    localStorage.setItem(key, connectionId);
  } else {
    localStorage.removeItem(key);
  }
}

export function getDefaultConnectionIdForUser(userId: string): string | null {
  return localStorage.getItem(`${DEFAULT_CONNECTION_ID_KEY_PREFIX}${userId}`);
}

export function clearDefaultConnectionIdForUser(userId: string | null | undefined): void {
  if (!userId) return;
  localStorage.removeItem(`${DEFAULT_CONNECTION_ID_KEY_PREFIX}${userId}`);
}
